"use client";
import './i18n/autoTranslate';

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Bot,
  CheckCircle,
  Cookie,
  Database,
  Eye,
  FileText,
  Lock,
  Mail,
  RefreshCw,
  Server,
  Shield,
  Sparkles,
  Trash2,
  User,
  Zap,
} from 'lucide-react';
import Globe from '../components/lightswind/globe';
import SEO from './SEO';

const EFFECT = [0.22, 1, 0.36, 1];

const SectionWrapper = ({ children, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.75, ease: EFFECT }}
    viewport={{ once: true }}
    className={className}
  >
    {children}
  </motion.div>
);

const GlassCard = ({ children, className = '', hover = true }) => (
  <motion.div
    whileHover={hover ? { y: -6, transition: { duration: 0.25 } } : {}}
    className={`relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_0_40px_rgba(108,99,255,0.08)] transition-all duration-300 ${
      hover ? 'hover:border-white/20 hover:bg-white/[0.08] hover:shadow-[0_0_60px_rgba(108,99,255,0.15)]' : ''
    } ${className}`}
  >
    <img
      src="/assets/images/Gemini_Generated_6_dlmovh.png"
      className="absolute inset-0 -z-10 h-full w-full object-cover opacity-[0.07]"
      alt=""
    />
    {children}
  </motion.div>
);

const PolicySection = ({ icon: Icon, title, children }) => (
  <GlassCard className="p-6 md:p-8" hover={false}>
    <div className="flex flex-col gap-5 md:flex-row md:items-start">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-gradient-to-br from-white/10 to-white/[0.03]">
        <Icon className="h-6 w-6 text-[#8B7CFF]" />
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="text-2xl font-bold text-white md:text-3xl">{title}</h2>
        <div className="mt-4 space-y-4 text-base leading-8 text-gray-300">{children}</div>
      </div>
    </div>
  </GlassCard>
);

const BulletList = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item) => (
      <li key={item} className="flex gap-3">
        <CheckCircle className="mt-1 h-4 w-4 shrink-0 text-[#8B7CFF]" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = 'May 1, 2026';

  const quickPoints = [
    { icon: User, title: 'Account data', text: 'We collect the information needed to create and secure your account.' },
    { icon: Bot, title: 'Interview data', text: 'We process your answers and practice data to generate feedback and analytics.' },
    { icon: Lock, title: 'Security first', text: 'We use technical and organizational safeguards to protect user information.' },
    { icon: Trash2, title: 'Your choices', text: 'You can request access, correction, or deletion of your personal data.' },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#07070A] text-white">
      <SEO
        title="Privacy Policy — InterviewGenieAI"
        description="Learn how InterviewGenieAI collects, uses, stores, protects, and shares personal data, including account details, interview practice content, and Google sign-in data."
        canonical="https://interviewgenieai.com/privacy-policy"
      />

      <div className="pointer-events-none absolute inset-0 opacity-20">
        <Globe
          size="full"
          theta={0.2}
          dark={1}
          scale={1.1}
          diffuse={1.5}
          baseColor="#1a1a1a"
          markerColor="#6C63FF"
          glowColor="#444444"
          className="absolute"
        />
      </div>
      <div className="pointer-events-none absolute left-1/4 top-32 h-96 w-96 rounded-full bg-[#6C63FF]/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-20 right-1/4 h-96 w-96 rounded-full bg-[#8B7CFF]/10 blur-3xl" />

      <section className="relative z-10 px-4 pb-16 pt-32 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 inline-flex items-center gap-3"
          >
            <div className="h-px w-8 bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
            <span className="text-sm font-medium uppercase tracking-wider text-[#8B7CFF]">Data Protection</span>
            <div className="h-px w-8 bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mb-8 text-5xl font-bold tracking-tight md:text-6xl lg:text-7xl"
          >
            <span className="bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent">Privacy</span>
            <br />
            <span className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">Policy</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mx-auto mb-6 max-w-3xl text-xl leading-relaxed text-gray-300"
          >
            This policy explains what data InterviewGenieAI collects, why we use it, how we protect it, and the choices you have when using our AI interview preparation platform.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-sm font-medium uppercase tracking-wider text-gray-500"
          >
            Last updated: {lastUpdated}
          </motion.p>
        </div>
      </section>

      <section className="relative z-10 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2 lg:grid-cols-4">
          {quickPoints.map((point, index) => {
            const Icon = point.icon;
            return (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: index * 0.08 }}
                viewport={{ once: true }}
              >
                <GlassCard className="h-full p-6">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/[0.04]">
                    <Icon className="h-6 w-6 text-[#8B7CFF]" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{point.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-gray-400">{point.text}</p>
                </GlassCard>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="relative z-10 px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl space-y-8">
          <SectionWrapper>
            <PolicySection icon={FileText} title="1. Who we are">
              <p>
                InterviewGenieAI is an AI-powered interview preparation platform available at{' '}
                <a href="https://interviewgenieai.com" className="text-[#8B7CFF] hover:text-white">
                  interviewgenieai.com
                </a>
                . This Privacy Policy applies to our website, dashboard, AI interview features, resume tools, analytics, and related services.
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Database} title="2. Information we collect">
              <p>We collect information that helps us create your account, deliver AI interview practice, improve the product, and keep the service secure.</p>
              <BulletList
                items={[
                  'Account information: name, email address, profile image, login provider, and account preferences.',
                  'Interview and learning content: answers, selected roles, industries, question categories, scores, feedback, progress, and practice history.',
                  'Resume and career content: resumes, job titles, skills, experience details, and content you choose to upload or enter.',
                  'Billing information: plan type, subscription status, payment events, invoices, and transaction identifiers. Payment card details are handled by payment providers, not stored directly by InterviewGenieAI.',
                  'Technical data: device type, browser, IP address, pages visited, log data, error reports, and approximate usage activity.',
                ]}
              />
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Sparkles} title="3. Google sign-in and Google user data">
              <p>
                If you choose to sign in with Google, we may receive basic profile information such as your name, email address, Google account identifier, and profile image. We use this information only to create your account, authenticate you, personalize your experience, prevent abuse, and provide support.
              </p>
              <BulletList
                items={[
                  'We do not sell Google user data.',
                  'We do not use Google user data for advertising.',
                  'We do not transfer Google user data except as needed to provide and secure the service, comply with law, or with your consent.',
                  'Our use and transfer of information received from Google APIs will comply with the Google API Services User Data Policy, including the Limited Use requirements.',
                ]}
              />
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Bot} title="4. How we use information">
              <BulletList
                items={[
                  'To create, secure, and manage your account.',
                  'To generate AI interview questions, feedback, summaries, reports, and progress analytics.',
                  'To personalize practice sessions based on your selected role, industry, interview type, resume, or previous performance.',
                  'To process subscriptions, enforce usage limits, and provide billing support.',
                  'To detect abuse, debug errors, monitor performance, and improve reliability.',
                  'To communicate with you about account activity, support requests, product updates, and important policy changes.',
                ]}
              />
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Server} title="5. AI providers and service providers">
              <p>
                We may use trusted infrastructure, hosting, database, analytics, payment, email, notification, voice, and AI service providers to operate InterviewGenieAI. These providers process information only as needed to deliver their services to us.
              </p>
              <p>
                When AI features are used, your prompts, answers, resume content, and interview context may be processed to generate feedback, questions, summaries, or analytics.
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Eye} title="6. Sharing of information">
              <p>We do not sell your personal information. We may share limited information in these situations:</p>
              <BulletList
                items={[
                  'With service providers that help us host, secure, analyze, bill, email, or operate the platform.',
                  'When required to comply with legal obligations or respond to valid legal requests.',
                  'To protect the rights, safety, and security of InterviewGenieAI, users, or the public.',
                  'In connection with a business transfer such as a merger, acquisition, financing, or sale of assets.',
                  'With your consent or direction, such as when you choose to share a report or export your data.',
                ]}
              />
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Cookie} title="7. Cookies and analytics">
              <p>
                We may use cookies, local storage, and similar technologies to keep you signed in, remember preferences, measure performance, understand usage, improve the user experience, and protect against abuse.
              </p>
              <p>
                You can control cookies through your browser settings, but some features may not work correctly if cookies or local storage are disabled.
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Lock} title="8. Data security and retention">
              <p>
                We use reasonable technical and organizational safeguards designed to protect your data from unauthorized access, loss, misuse, or alteration. No online service can guarantee complete security, but we work to keep InterviewGenieAI safe and reliable.
              </p>
              <p>
                We keep personal information only as long as needed for the purposes described in this policy, including account management, legal compliance, dispute resolution, security, backups, and product improvement.
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Trash2} title="9. Your privacy choices">
              <p>Depending on your location and applicable law, you may have rights to:</p>
              <BulletList
                items={[
                  'Access the personal information we hold about you.',
                  'Correct inaccurate account or profile information.',
                  'Request deletion of your account or personal information.',
                  'Object to or restrict certain processing activities.',
                  'Request a copy of certain data in a portable format.',
                  'Withdraw consent where processing is based on consent.',
                ]}
              />
              <p>
                To make a request, contact us at{' '}
                <a href="mailto:support@interviewgenieai.com" className="text-[#8B7CFF] hover:text-white">
                  support@interviewgenieai.com
                </a>
                .
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Shield} title="10. Children’s privacy">
              <p>
                InterviewGenieAI is intended for users preparing for education, internship, and career opportunities. If you are under the age required to consent to online services in your location, you should use the platform only with permission from a parent, guardian, school, or authorized organization.
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={RefreshCw} title="11. Changes to this policy">
              <p>
                We may update this Privacy Policy as our product, data practices, or legal requirements change. When we make important changes, we will update the “Last updated” date and may provide additional notice inside the product or by email.
              </p>
            </PolicySection>
          </SectionWrapper>

          <SectionWrapper>
            <PolicySection icon={Mail} title="12. Contact us">
              <p>
                For privacy questions or requests, contact us at{' '}
                <a href="mailto:support@interviewgenieai.com" className="text-[#8B7CFF] hover:text-white">
                  support@interviewgenieai.com
                </a>
                .
              </p>
            </PolicySection>
          </SectionWrapper>
        </div>
      </section>

      <section className="relative z-10 px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl">
          <SectionWrapper>
            <GlassCard className="p-10 text-center md:p-12">
              <h2 className="text-3xl font-bold text-white md:text-5xl">Your data should support your growth.</h2>
              <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-gray-300">
                InterviewGenieAI uses your practice data to help you improve, personalize your sessions, and build confidence before real interviews.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
                <button
                  onClick={() => navigate('/pricing')}
                  className="inline-flex items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] px-8 py-4 font-semibold text-white transition-all duration-300 hover:shadow-[0_0_50px_rgba(108,99,255,0.45)]"
                >
                  <Zap className="h-5 w-5" />
                  Start Practicing
                  <ArrowRight className="h-5 w-5" />
                </button>
                <Link
                  to="/terms-of-service"
                  className="inline-flex items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/5 px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-white/10"
                >
                  Terms of Service
                </Link>
              </div>
            </GlassCard>
          </SectionWrapper>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/10 bg-black/30 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 md:flex-row">
          <div className="flex items-center gap-3">
            <img src="/GenieAI_2.png" alt="InterviewGenieAI" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-bold text-white">InterviewGenieAI</div>
              <div className="text-sm text-gray-500">AI interview preparation platform</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <Link to="/about" className="text-gray-400 transition-colors hover:text-[#8B7CFF]">About</Link>
            <Link to="/privacy-policy" className="text-gray-400 transition-colors hover:text-[#8B7CFF]">Privacy</Link>
            <Link to="/terms-of-service" className="text-gray-400 transition-colors hover:text-[#8B7CFF]">Terms</Link>
            <Link to="/contact" className="text-gray-400 transition-colors hover:text-[#8B7CFF]">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
